import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  useMediaQuery,
  useTheme,
  FormControlLabel,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  useLazyGetBankDetailsQuery,
  useCreateBankDetailsMutation,
  useUpdateBankDetailsMutation,
  useDownloadExcelMutation,
} from '../services/bankApi';
import { BankFormDialog } from '../components/BankFormDialog';
import { BankHistoryDialog } from '../components/BankHistoryDialog';
import { AggregatorOption, BankRow, getGatewayParam, mapToBankRow } from '../types/bankdetails';

export default function BankDetails() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  // URL Params
  const searchParamsValues = useMemo(
    () => ({
      page: parseInt(searchParams.get('page') || '1', 10),
      pageSize: parseInt(searchParams.get('pageSize') || '20', 10),
      aggregator: (searchParams.get('aggregator') as AggregatorOption) || 'Billdesk V1',
      ifscFilter: searchParams.get('ifsc') || '',
    }),
    [searchParams]
  );

  const { page, pageSize, aggregator, ifscFilter } = searchParamsValues;

  // State
  const [rows, setRows] = useState<BankRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [selectedBankForHistory, setSelectedBankForHistory] = useState<BankRow | null>(null);
  const [ifscInputValue, setIfscInputValue] = useState(ifscFilter); // Separate input value from filter

  // API Hooks
  const [triggerGetBankDetails, { isFetching }] = useLazyGetBankDetailsQuery();
  const [createBankDetails] = useCreateBankDetailsMutation();
  const [updateBankDetails] = useUpdateBankDetailsMutation();
  const [downloadExcel] = useDownloadExcelMutation();

  // Update Search Params
  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === '' || value === undefined) {
            newParams.delete(key);
          } else {
            newParams.set(key, value);
          }
        });
        return newParams;
      });
    },
    [setSearchParams]
  );

  // Debounced IFSC Search
  const debouncedIfscSearch = useMemo(
    () =>
      debounce((value: string) => {
        updateSearchParams({ ifsc: value || null, page: '1' });
      }, 500),
    [updateSearchParams]
  );

  // Handle IFSC input change
  const handleIfscInputChange = useCallback(
    (value: string) => {
      setIfscInputValue(value); // Update input value immediately for UI
      debouncedIfscSearch(value); // Debounce the actual API call
    },
    [debouncedIfscSearch]
  );

  // Sync input value with URL params
  useEffect(() => {
    setIfscInputValue(ifscFilter);
  }, [ifscFilter]);

  // Handlers
  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParams({ page: newPage.toString() });
    },
    [updateSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      updateSearchParams({ pageSize: newPageSize.toString(), page: '1' });
    },
    [updateSearchParams]
  );

  const handleAggregatorChange = useCallback(
    (newAggregator: AggregatorOption) => {
      updateSearchParams({ aggregator: newAggregator, page: '1' });
    },
    [updateSearchParams]
  );

  useEffect(() => {
    const fetchData = async () => {
      const res = await triggerGetBankDetails({
        gateway: getGatewayParam(aggregator),
        ifsc: ifscFilter.trim() || undefined,
        page,
        limit: pageSize,
      });
      if ('data' in res && res.data) {
        const payload: any = res.data;
        const raw = payload.data ?? payload;
        const mapped: BankRow[] = Array.isArray(raw)
          ? raw.map((it: any, idx: number) => mapToBankRow(it, idx))
          : [];
        setRows(mapped);
        setTotalCount(payload.meta?.total ?? payload.data?.length ?? 0);
      }
    };
    fetchData();
  }, [triggerGetBankDetails, aggregator, ifscFilter, page, pageSize]);

  const filteredRows = useMemo(() => rows, [rows]);

  const handleDownload = async () => {
    try {
      const gateway = getGatewayParam(aggregator);
      const blob = await downloadExcel({
        gateway,
        ifsc: ifscFilter.trim() || undefined,
        bankName: undefined,
      }).unwrap();
      const dlUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = dlUrl;
      link.setAttribute('download', 'bank-details.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      console.error('Download failed', e);
    }
  };

  const handleAddBank = async (formData: any) => {
    if (!formData.name || !formData.ifscCode) {
      return;
    }

    try {
      const bankData: any = {
        gateway: getGatewayParam(aggregator),
        ifsc: formData.ifscCode,
        bankName: formData.name,
        remarks: formData.remarks,
        isActive: true,
        lumpsumNetBanking: !!formData.nbLumpsum,
        hybridNetBanking: !!formData.nbHybrid,
        lumpsumDebitCard: !!formData.nbDebitCard,
        emandateNetBanking: !!formData.emdNetBanking,
        emandateDebitCard: !!formData.emdDebitCard,
        emandateAadhar: !!formData.emdAadhaar,
        emandatePAN: !!formData.emdPan,
        otmNetBanking: !!formData.emdNetBanking,
        otmDebitCard: !!formData.emdDebitCard,
        lumpsumNetBankingBankId: formData.nbBankId || '',
        hybridNetBankingBankId: formData.nbHybridBankId || '',
        lumpsumDebitCardBankId: formData.nbDebitCardBankId || '',
        emandateBankId: formData.emdBankId || '',
      };

      await createBankDetails(bankData).unwrap();
      setOpenAddDialog(false);
      // Refresh the data
      const res = await triggerGetBankDetails({
        gateway: getGatewayParam(aggregator),
        ifsc: ifscFilter.trim() || undefined,
        page,
        limit: pageSize,
      });
      if ('data' in res && res.data) {
        const payload: any = res.data;
        const raw = payload.data ?? payload;
        const mapped: BankRow[] = Array.isArray(raw)
          ? raw.map((it: any, idx: number) => mapToBankRow(it, idx))
          : [];
        setRows(mapped);
        setTotalCount(payload.meta?.total ?? payload.data?.length ?? 0);
      }
    } catch (error) {
      console.error('Failed to create bank:', error);
    }
  };

  const handleUpdateBank = async (formData: any) => {
    if (!formData.name || !formData.ifscCode || editingIndex === null) {
      return;
    }

    try {
      const current = rows[editingIndex];

      // Always include id, gateway, ifsc and remarks
      const bankData: any = {
        id: current.id.toString(),
        gateway: getGatewayParam(aggregator),
        ifsc: formData.ifscCode,
        remarks: formData.remarks,
      };

      // Conditionally include only changed fields
      if (typeof formData.name !== 'undefined' && formData.name !== current.name) {
        bankData.bankName = formData.name;
      }

      // Track whether any fields in each category changed
      const lumpsumCategoryChanged =
        typeof formData.nbLumpsum !== 'undefined' &&
        Boolean(formData.nbLumpsum) !== Boolean(current.netbanking?.lumpsum);
      const lumpsumBankIdChanged =
        typeof formData.nbBankId !== 'undefined' &&
        formData.nbBankId !== (current.netbanking?.bankId ?? '');
      const hybridCategoryChanged =
        typeof formData.nbHybrid !== 'undefined' &&
        Boolean(formData.nbHybrid) !== Boolean(current.netbanking?.hybrid);
      const hybridBankIdChanged =
        typeof formData.nbHybridBankId !== 'undefined' &&
        formData.nbHybridBankId !== (current.netbanking?.hybridBankId ?? '');
      const debitCardCategoryChanged =
        typeof formData.nbDebitCard !== 'undefined' &&
        Boolean(formData.nbDebitCard) !== Boolean(current.netbanking?.lumpsumDebitCard);
      const debitCardBankIdChanged =
        typeof formData.nbDebitCardBankId !== 'undefined' &&
        formData.nbDebitCardBankId !== (current.netbanking?.lumpsumDebitCardBankId ?? '');
      const emandateCategoryChanged =
        (typeof formData.emdNetBanking !== 'undefined' &&
          Boolean(formData.emdNetBanking) !== Boolean(current.emandate?.netBanking)) ||
        (typeof formData.emdDebitCard !== 'undefined' &&
          Boolean(formData.emdDebitCard) !== Boolean(current.emandate?.debitCard)) ||
        (typeof formData.emdAadhaar !== 'undefined' &&
          Boolean(formData.emdAadhaar) !== Boolean(current.emandate?.aadhaar)) ||
        (typeof formData.emdPan !== 'undefined' &&
          Boolean(formData.emdPan) !== Boolean(current.emandate?.pan));
      const emandateBankIdChanged =
        typeof formData.emdBankId !== 'undefined' &&
        formData.emdBankId !== (current.emandate?.bankId ?? '');

      // If a category changed, include its bankId mandatorily
      if (lumpsumCategoryChanged || lumpsumBankIdChanged) {
        bankData.lumpsumNetBankingBankId =
          formData.nbBankId ?? (current.netbanking?.bankId ?? '');
      }

      if (hybridCategoryChanged || hybridBankIdChanged) {
        bankData.hybridNetBankingBankId =
          formData.nbHybridBankId ?? (current.netbanking?.hybridBankId ?? '');
      }

      if (debitCardCategoryChanged || debitCardBankIdChanged) {
        bankData.lumpsumDebitCardBankId =
          formData.nbDebitCardBankId ?? (current.netbanking?.lumpsumDebitCardBankId ?? '');
      }

      if (emandateCategoryChanged || emandateBankIdChanged) {
        bankData.emandateBankId = formData.emdBankId ?? (current.emandate?.bankId ?? '');
      }

      if (
        typeof formData.nbLumpsum !== 'undefined' &&
        Boolean(formData.nbLumpsum) !== Boolean(current.netbanking?.lumpsum)
      ) {
        bankData.lumpsumNetBanking = !!formData.nbLumpsum;
      }

      if (
        typeof formData.nbHybrid !== 'undefined' &&
        Boolean(formData.nbHybrid) !== Boolean(current.netbanking?.hybrid)
      ) {
        bankData.hybridNetBanking = !!formData.nbHybrid;
      }

      if (
        typeof formData.nbDebitCard !== 'undefined' &&
        Boolean(formData.nbDebitCard) !== Boolean(current.netbanking?.lumpsumDebitCard)
      ) {
        bankData.lumpsumDebitCard = !!formData.nbDebitCard;
      }

      if (
        typeof formData.emdNetBanking !== 'undefined' &&
        Boolean(formData.emdNetBanking) !== Boolean(current.emandate?.netBanking)
      ) {
        bankData.emandateNetBanking = !!formData.emdNetBanking;
      }

      if (
        typeof formData.emdDebitCard !== 'undefined' &&
        Boolean(formData.emdDebitCard) !== Boolean(current.emandate?.debitCard)
      ) {
        bankData.emandateDebitCard = !!formData.emdDebitCard;
      }

      if (
        typeof formData.emdAadhaar !== 'undefined' &&
        Boolean(formData.emdAadhaar) !== Boolean(current.emandate?.aadhaar)
      ) {
        bankData.emandateAadhar = !!formData.emdAadhaar;
      }

      if (
        typeof formData.emdPan !== 'undefined' &&
        Boolean(formData.emdPan) !== Boolean(current.emandate?.pan)
      ) {
        bankData.emandatePAN = !!formData.emdPan;
      }

      await updateBankDetails(bankData).unwrap();
      setOpenEditDialog(false);
      setEditingIndex(null);
      // Refresh the data
      const res = await triggerGetBankDetails({
        gateway: getGatewayParam(aggregator),
        ifsc: ifscFilter.trim() || undefined,
        page,
        limit: pageSize,
      });
      if ('data' in res && res.data) {
        const payload: any = res.data;
        const raw = payload.data ?? payload;
        const mapped: BankRow[] = Array.isArray(raw)
          ? raw.map((it: any, idx: number) => mapToBankRow(it, idx))
          : [];
        setRows(mapped);
        setTotalCount(payload.meta?.total ?? payload.data?.length ?? 0);
      }
    } catch (error) {
      console.error('Failed to update bank:', error);
    }
  };

  const openEditDialogForBank = (row: BankRow) => {
    setEditingIndex(rows.findIndex((r) => r.id === row.id));
    setOpenEditDialog(true);
  };

  const openHistoryDialogForBank = (bank: BankRow) => {
    setSelectedBankForHistory(bank);
    setOpenHistoryDialog(true);
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Breadcrumbs
        separator="·"
        sx={{
          mb: 1,
          '& .MuiBreadcrumbs-separator': { mx: 1, color: theme.palette.text.secondary },
        }}
      >
        <Link
          component={RouterLink}
          to="/dashboard"
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.subtitle1.fontWeight,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Dashboard
        </Link>
        <Typography variant="subtitle1">Bank Details</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
          p: 2,
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Payment Gateways
        </Typography>
        <RadioGroup
          row
          value={aggregator}
          onChange={(e) => handleAggregatorChange(e.target.value as AggregatorOption)}
        >
          <FormControlLabel value="Billdesk V1" control={<Radio />} label="Billdesk V1" />
          <FormControlLabel value="Billdesk V2" control={<Radio />} label="Billdesk V2" />
          <FormControlLabel value="CAMS Pay" control={<Radio />} label="CAMS Pay" />
          <FormControlLabel value="Kotak" control={<Radio />} label="KOTAK" />
          <FormControlLabel value="CashFree" control={<Radio />} label="CashFree" />
        </RadioGroup>

        <Box
          sx={{
            mt: 2,
            display: 'flex',
            gap: 2,
            flexDirection: isMobile ? 'column' : 'row',
            maxWidth: '100%',
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Filter by IFSC"
            value={ifscInputValue}
            onChange={(e) => handleIfscInputChange(e.target.value)}
          />
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: '16px' }}>
        <Table
          size="small"
          sx={{
            '& th, & td': { border: `1px solid ${theme.palette.divider}` },
            '& thead th': { backgroundColor: theme.palette.action.hover, textAlign: 'center' },
          }}
        >
          <TableHead>
            <TableRow sx={{ '& th': { borderBottom: `2px solid ${theme.palette.divider}` } }}>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell align="center" colSpan={6} sx={{ fontWeight: 600 }}>
                Netbanking
              </TableCell>
              <TableCell align="center" colSpan={5} sx={{ fontWeight: 600 }}>
                Emandate / OTM
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>S.No</TableCell>
              <TableCell>Bank Name</TableCell>
              <TableCell>Bank IFSC Code</TableCell>
              <TableCell>Bank ID</TableCell>
              <TableCell align="center">Lumpsum</TableCell>
              <TableCell>Bank ID</TableCell>
              <TableCell align="center">Hybrid / iSIP</TableCell>
              <TableCell>Bank ID</TableCell>
              <TableCell align="center">Debit Card</TableCell>
              <TableCell>Bank ID</TableCell>
              <TableCell align="center">Net Banking</TableCell>
              <TableCell align="center">Debit Card</TableCell>
              <TableCell align="center">Aadhaar</TableCell>
              <TableCell align="center">PAN</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={16} align="center" sx={{ py: 3 }}>
                  No banks found{ifscFilter ? ` for "${ifscFilter}"` : ''}
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row, idx) => (
                <TableRow key={row.id} hover>
                  <TableCell>{(page - 1) * pageSize + idx + 1}</TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      onClick={() => openEditDialogForBank(row)}
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        p: 0,
                        fontSize: theme.typography.subtitle1.fontSize,
                        fontWeight: theme.typography.subtitle1.fontWeight,
                      }}
                    >
                      {row.name}
                    </Link>
                  </TableCell>
                  <TableCell>{row.ifscCode}</TableCell>
                  <TableCell>{row.netbanking?.bankId || '-'}</TableCell>
                  <TableCell align="center">{row.netbanking?.lumpsum ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{row.netbanking?.hybridBankId || '-'}</TableCell>
                  <TableCell align="center">{row.netbanking?.hybrid ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{row.netbanking?.lumpsumDebitCardBankId || '-'}</TableCell>
                  <TableCell align="center">{row.netbanking?.lumpsumDebitCard ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{row.emandate?.bankId || '-'}</TableCell>
                  <TableCell align="center">{row.emandate?.netBanking ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="center">{row.emandate?.debitCard ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="center">{row.emandate?.aadhaar ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="center">{row.emandate?.pan ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 2,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownload}
          sx={{ flex: isMobile ? 1 : 'none' }}
        >
          Download as Excel
        </Button>
        {isFetching && (
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Loading...
          </Typography>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setOpenAddDialog(true)}
          sx={{ flex: isMobile ? 1 : 'none' }}
        >
          Add New Bank
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to{' '}
          {Math.min(page * pageSize, totalCount)} of {totalCount} entries
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Per page"
            select
            SelectProps={{ native: true }}
            size="small"
            value={pageSize}
            onChange={(e) =>
              handlePageSizeChange(parseInt(e.target.value as unknown as string, 10))
            }
            sx={{ width: 120 }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </TextField>
          <Pagination
            count={Math.max(1, Math.ceil((totalCount || 0) / pageSize))}
            page={page}
            onChange={(_e, value) => handlePageChange(value)}
            showFirstButton
            showLastButton
            size="small"
          />
        </Box>
      </Box>

      {/* Add New Bank Dialog */}
      <BankFormDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        title="Add New Bank"
        onSubmit={handleAddBank}
        aggregator={aggregator}
        mode="add"
      />

      {/* Edit Bank Dialog */}
      <BankFormDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        title="Update Bank Details"
        onSubmit={handleUpdateBank}
        aggregator={aggregator}
        mode="edit"
        editingIndex={editingIndex}
        rows={rows}
        onOpenHistory={openHistoryDialogForBank}
      />

      {/* Bank History Dialog */}
      <BankHistoryDialog
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        selectedBank={selectedBankForHistory}
        aggregator={aggregator}
      />
    </Box>
  );
}
